import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Activity, BookOpen, Boxes, FileCode, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useScenarios } from "@/api/scenarios";
import { useBackends } from "@/api/backends";
import { useRuns } from "@/api/runs";
import { useTheme } from "@/hooks/useTheme";
import { RunStatusBadge } from "@/features/runs/RunStatusBadge";
import neubirdMarkLight from "@/assets/neubird-mark-light.svg";
import neubirdMarkDark from "@/assets/neubird-mark-dark.svg";

export function HomePage() {
  const { theme } = useTheme();
  const scenarios = useScenarios();
  const backends = useBackends();
  const runs = useRuns();

  const scenarioList = scenarios.data ?? [];
  const backendList = backends.data ?? [];
  // Sort runs newest-first so the "Recent runs" slice actually shows the
  // most recent 3, not whatever order the API/store happens to return.
  const runList = [...(runs.data ?? [])].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );

  return (
    <div className="p-6 space-y-8 max-w-5xl">
      {/* Band 1 — Hero */}
      <section className="flex items-center gap-6">
        <img
          src={theme === "dark" ? neubirdMarkDark : neubirdMarkLight}
          alt=""
          aria-hidden
          className="w-40 h-32 flex-shrink-0 object-contain"
        />
        <div className="space-y-2 flex-1">
          <h1 className="text-5xl font-semibold tracking-tight text-foreground">demosim</h1>
          <p className="text-muted-foreground">
            NeuBird agent runner — synthetic observability, real backends.
          </p>
          <div className="pt-2">
            <Button asChild>
              <Link to="/scenarios">Start a run</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Band 2 — Quick launcher */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileText size={14} className="text-muted-foreground" />
            <span>
              {scenarios.isLoading || scenarios.isError ? "" : `${scenarioList.length} `}
              scenarios
            </span>
          </div>
          {scenarios.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : scenarios.isError ? (
            <p className="text-xs text-destructive">Couldn't load scenarios.</p>
          ) : scenarioList.length === 0 ? (
            <p className="text-xs text-muted-foreground">No scenarios yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {scenarioList.slice(0, 3).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/scenarios/${s.slug}`}
                    className="text-foreground hover:underline underline-offset-2"
                  >
                    {s.name || s.slug}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-1">
            <Link
              to="/scenarios"
              className="text-xs text-primary underline-offset-2 hover:underline"
            >
              Browse all →
            </Link>
          </div>
        </Card>

        <Card className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Boxes size={14} className="text-muted-foreground" />
            <span>
              {backends.isLoading || backends.isError ? "" : `${backendList.length} `}
              backends
            </span>
          </div>
          {backends.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : backends.isError ? (
            <p className="text-xs text-destructive">Couldn't load backends.</p>
          ) : backendList.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No backends yet — add one before running a scenario.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {backendList.slice(0, 6).map((b) => (
                <span
                  key={b.id}
                  className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-mono border border-border bg-muted/40 text-muted-foreground"
                  title={b.name}
                >
                  {b.kind}
                </span>
              ))}
            </div>
          )}
          <div className="pt-1">
            <Link
              to="/backends"
              className="text-xs text-primary underline-offset-2 hover:underline"
            >
              Configure backends →
            </Link>
          </div>
        </Card>

        <Card className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Activity size={14} className="text-muted-foreground" />
            <span>Recent runs</span>
          </div>
          {runs.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : runs.isError ? (
            <p className="text-xs text-destructive">Couldn't load runs.</p>
          ) : runList.length === 0 ? (
            <p className="text-xs text-muted-foreground">No runs yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {runList.slice(0, 3).map((r) => (
                <li key={r.id} className="flex items-center gap-2">
                  <RunStatusBadge status={r.status} />
                  <Link
                    to={`/runs/${r.id}`}
                    className="text-foreground hover:underline underline-offset-2 truncate"
                  >
                    {r.scenario_id}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-1">
            <Link to="/runs" className="text-xs text-primary underline-offset-2 hover:underline">
              See all runs →
            </Link>
          </div>
        </Card>
      </section>

      {/* Band 3 — Docs launcher */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Get oriented
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DocsCard
            to="/docs/getting-started"
            icon={<BookOpen size={16} />}
            title="Getting Started"
            description="Log in, add a backend, pick a scenario, click Rebase."
          />
          <DocsCard
            to="/docs/concepts"
            icon={<Boxes size={16} />}
            title="Concepts"
            description="Scenarios, runs, backends, templates, multi-backend routing."
          />
          <DocsCard
            to="/docs/scenario-authoring"
            icon={<FileCode size={16} />}
            title="Scenario Authoring"
            description="Use Claude Code with existing scenarios as templates."
          />
        </div>
        <div className="mt-3 text-sm">
          <Link to="/docs" className="text-primary underline-offset-2 hover:underline">
            Full docs →
          </Link>
        </div>
      </section>
    </div>
  );
}

function DocsCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="block p-4 rounded-md border border-border bg-card hover:bg-muted/40 transition-colors"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        {icon}
        <span>{title}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </Link>
  );
}
